import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

export type ProcessingFeeType = 'card' | 'ach'

export type ProcessingFeeMode = 'percent' | 'flat'

export interface ProcessingFee {
  // Note: in cents
  flatAmount?: number | null
  mode?: ProcessingFeeMode
  percentage?: number | null
  type: ProcessingFeeType
}

export interface ChargeCodeMapping {
  code: string
  description: string
}

export interface PaymentSettingsValues {
  allowAutopay: boolean
  allowChargesSelection: boolean
  allowPartialPayment: boolean
  amenityChargeCode: string | null
  dailyBatch: boolean
  prePaidChargeCode: string | null
  processingDelay: number
  syncAmenityCharges: boolean
}

export interface PaymentSettings {
  chargeCodesMappings: ChargeCodeMapping[]
  processingFees: ProcessingFee[]
  settings: Partial<PaymentSettingsValues>
}

export const USE_GET_BUILDING_PAYMENT_SETTINGS_QUERY_KEY = 'useGetBuildingPaymentSettings'

export const getBuildingPaymentSettings = async (buildingId: number, signal?: AbortSignal) => {
  const response = await authAPI.get<PaymentSettings>(`/building/${buildingId}/payment-settings`, {
    signal,
  })
  return response.data
}

export const useGetBuildingPaymentSettings = (buildingId: number, enabled = true) =>
  useQuery({
    enabled,
    queryFn: ({ signal }) => getBuildingPaymentSettings(buildingId, signal),
    queryKey: [USE_GET_BUILDING_PAYMENT_SETTINGS_QUERY_KEY, buildingId],
  })

export const useInvalidateBuildingPaymentSettings = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: [USE_GET_BUILDING_PAYMENT_SETTINGS_QUERY_KEY] })
}

export interface UpdateBuildingPaymentSettingsRequest {
  buildingId: number
  data: PaymentSettings
}

export const updateBuildingPaymentSettings = async ({
  buildingId,
  data,
}: UpdateBuildingPaymentSettingsRequest) => {
  const response = await authAPI.put<PaymentSettings>(
    `/building/${buildingId}/payment-settings`,
    data,
  )
  return response.data
}

export const useUpdateBuildingPaymentSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateBuildingPaymentSettings,
    onSuccess: (settings, { buildingId }) =>
      queryClient.setQueryData([USE_GET_BUILDING_PAYMENT_SETTINGS_QUERY_KEY, buildingId], settings),
  })
}
